import { zodResolver } from '@hookform/resolvers/zod';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Vehicle } from 'further-types/lp/vehicle';
import { realisation as rules } from 'further-ui/validations';
import FormWithStateContextProvider from 'contexts/FormWithStateContext';
import useSessionStorage from 'hooks/ui/useSessionStorage';
import { RealisationFormState } from './types';
import { realisationFormStorageKey } from './constants';

type Props = {
  selectedVehicle: Vehicle;
  values: rules.CreateRealisationFormValues;
};

const FormWrapper: React.FC<PropsWithChildren<Props>> = ({
  children,
  selectedVehicle,
  values,
}) => {
  const storage = useSessionStorage<rules.CreateRealisationFormValues>(
    realisationFormStorageKey(selectedVehicle._id),
  );

  const formMethods = useForm<rules.CreateRealisationFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(rules.CreateRealisationFormSchema),
    defaultValues: storage.getData() ?? values,
    criteriaMode: 'all',
    resetOptions: {
      keepDefaultValues: false,
    },
  });

  const saveDataRef = useRef(storage.saveData);
  saveDataRef.current = storage.saveData;

  useEffect(() => {
    const subscription = formMethods.watch((formValues) => {
      saveDataRef.current(formValues as rules.CreateRealisationFormValues);
    });
    return () => subscription.unsubscribe();
  }, [formMethods]);

  // Initial state for the context
  const initialState: RealisationFormState = {
    consideration: values.consideration.map((_, index) => ({
      index,
      showAllocationsTable: false,
      disableAllocationsInputs: false,
      holdingClassInvestmentsConsiderationValues: [],
      investorAllocationsTotals: [],
    })),
  };

  return (
    <FormWithStateContextProvider
      formMethods={formMethods}
      initialState={initialState}
    >
      {children}
    </FormWithStateContextProvider>
  );
};

export default FormWrapper;
